/**
 * Reactive wrapper for the capability model in `src/lib/permissions.ts`.
 *
 * Recomputes whenever any underlying context value flips — role change,
 * shop switch, plan upgrade/downgrade, Front Desk toggle, apprentice
 * promotion (canTattoo flag flipping), or platform-admin grant.
 *
 * Prefer this over scattered `currentRole === "owner"` / `isAdmin`
 * checks. Adding a new role or shifting one permission between roles
 * should only touch `permissions.ts`, not every component.
 *
 * Usage
 * -----
 *   const { can } = useCapabilities();
 *   if (!can("view_finance")) return null;
 *
 *   <Button disabled={!can("send_payouts")}>Send Payouts</Button>
 *
 * For route-level gating prefer the route guard (e.g.
 * `<ProtectedRoute requireCapability="view_finance">…</ProtectedRoute>`
 * once that's wired in App.tsx). Use this hook for inline UI like
 * cards, buttons, and conditional sections.
 */
import { useCallback, useMemo } from "react";

import { useAuth } from "@/hooks/useAuth";
import { useSoloArtistLimits } from "@/hooks/useSoloArtistLimits";
import { useFrontDesk } from "@/contexts/FrontDeskContext";
import { usePlatformAdmin } from "@/hooks/usePlatformAdmin";
import { useApprenticeCanTattoo } from "@/hooks/useApprenticeCanTattoo";
import {
  computeCapabilities,
  type Capability,
} from "@/lib/permissions";

export function useCapabilities() {
  const { currentRole, currentShopId, memberships, isApprentice } = useAuth();
  const {
    isFree: isSoloFree,
    isBooker: isSoloBooker,
    isProPlus: isSoloProPlus,
  } = useSoloArtistLimits();
  const { isFrontDeskMode } = useFrontDesk();
  const { isPlatformAdmin } = usePlatformAdmin();
  const { canTattoo } = useApprenticeCanTattoo();

  // Find the membership row for the currently-active shop so we can
  // read `is_also_artist`. Pull it out so memo deps are explicit.
  const currentMembership = useMemo(
    () => memberships.find((m) => m.shop_id === currentShopId) ?? null,
    [memberships, currentShopId],
  );
  const isAlsoArtist = currentMembership?.is_also_artist ?? false;
  const membershipsLength = memberships.length;

  const caps = useMemo(
    () =>
      computeCapabilities({
        role: currentRole,
        isSoloArtist: membershipsLength === 0,
        isSoloFree,
        isSoloBooker,
        isSoloProPlus,
        isApprentice,
        apprenticeCanTattoo: canTattoo,
        isFrontDeskMode,
        isPlatformAdmin,
        isAlsoArtist,
      }),
    [
      currentRole,
      membershipsLength,
      isSoloFree,
      isSoloBooker,
      isSoloProPlus,
      isApprentice,
      canTattoo,
      isFrontDeskMode,
      isPlatformAdmin,
      isAlsoArtist,
    ],
  );

  const can = useCallback(
    (capability: Capability) => caps.has(capability),
    [caps],
  );

  return { caps, can };
}
